import type { Requirement } from '@ispot/shared'
import { REQUIREMENT_PRIORITY_LABELS, REQUIREMENT_STATUS_LABELS } from '../constants'

export interface DocumentStats {
  byType: Record<'epic' | 'story' | 'nfr-category', number>
  priorityRows: Array<{ label: string; count: number; color: string }>
  priorityTotal: number
  statusRows: Array<{ label: string; count: number; color: string }>
  statusTotal: number
  completenessAvg: number
  completenessPopulated: number
  total: number
}

export function computeDocumentStats(requirements: Requirement[]): DocumentStats {
  const byType: DocumentStats['byType'] = { epic: 0, story: 0, 'nfr-category': 0 }
  const priorityCounts: Record<string, number> = {}
  const statusCounts: Record<string, number> = {}
  let completenessSum = 0
  let completenessPopulated = 0

  for (const r of requirements) {
    byType[r.type] = (byType[r.type] ?? 0) + 1
    if (r.priority) priorityCounts[r.priority] = (priorityCounts[r.priority] ?? 0) + 1
    statusCounts[r.status] = (statusCounts[r.status] ?? 0) + 1
    if (r.completeness && r.completeness.total > 0) {
      completenessSum += r.completeness.completed / r.completeness.total
      completenessPopulated++
    }
  }

  const priorityTotal = Object.values(priorityCounts).reduce((a, b) => a + b, 0)
  const statusTotal = Object.values(statusCounts).reduce((a, b) => a + b, 0)

  return {
    byType,
    priorityRows: [
      { label: REQUIREMENT_PRIORITY_LABELS.critical, count: priorityCounts.critical ?? 0, color: 'var(--color-danger)' },
      { label: REQUIREMENT_PRIORITY_LABELS.high,     count: priorityCounts.high     ?? 0, color: 'var(--color-warning)' },
      { label: REQUIREMENT_PRIORITY_LABELS.medium,   count: priorityCounts.medium   ?? 0, color: 'var(--color-warm-600)' },
      { label: REQUIREMENT_PRIORITY_LABELS.low,      count: priorityCounts.low      ?? 0, color: 'var(--color-warm-400)' },
    ],
    priorityTotal,
    statusRows: [
      { label: REQUIREMENT_STATUS_LABELS['in-progress'], count: statusCounts['in-progress'] ?? 0, color: 'var(--color-info)' },
      { label: REQUIREMENT_STATUS_LABELS.planned,        count: statusCounts.planned        ?? 0, color: 'var(--color-warm-500)' },
      { label: REQUIREMENT_STATUS_LABELS.backlog,        count: statusCounts.backlog        ?? 0, color: 'var(--color-warm-400)' },
      { label: REQUIREMENT_STATUS_LABELS['in-review'],   count: statusCounts['in-review']   ?? 0, color: 'var(--color-warning)' },
      { label: REQUIREMENT_STATUS_LABELS.done,           count: statusCounts.done           ?? 0, color: 'var(--color-success)' },
    ],
    statusTotal,
    completenessAvg: completenessPopulated > 0 ? completenessSum / completenessPopulated : 0,
    completenessPopulated,
    total: requirements.length,
  }
}
